import React from "react";
import { Helmet } from "react-helmet";
import { Shield, Scale, Zap, Globe, FileText } from "lucide-react";

export default function TermsOmoreGames() {
  const sections = [
    {
      icon: <Zap className="w-5 h-5 text-blue-500" />,
      title: "Using Our Games",
      desc: "Our games are for your personal entertainment. Please do not try to hack, copy, or break the games. We build these for everyone to enjoy fairly."
    },
    {
      icon: <Shield className="w-5 h-5 text-blue-500" />,
      title: "Your Account",
      desc: "You are responsible for how you use the site. If we find anyone misusing the system or cheating in reflex tests, we may block their access."
    },
    {
      icon: <Scale className="w-5 h-5 text-blue-500" />,
      title: "Ownership",
      desc: "OMORE GAMES owns all the code, art, and names. You cannot use our logo or game assets for your own business without our permission."
    },
    {
      icon: <Globe className="w-5 h-5 text-blue-500" />,
      title: "Service Changes",
      desc: "We might update games or take them offline for maintenance. We aren't responsible if a game is temporarily unavailable."
    }
  ];

  return (
    <div className="min-h-screen bg-[#090a0b] text-white selection:bg-blue-500/30 font-sans">
<Helmet>

  <html lang="en" />

  <title>
    Terms of Service | OMORE Games Rules, Usage Policy & Neuralis Guidelines
  </title>

  <meta
    name="description"
    content="Read the official Terms of Service for OMORE Games. Learn the rules for using Neuralis, reflex tests, and browser brain games created by OMORE Games."
  />

  <meta
    name="keywords"
    content="
    omore games terms,
    neuralis terms,
    reflex game rules,
    brain game policy,
    indie game terms of service,
    neuralis rules,
    omore games policy,
    browser game terms,
    reaction time test rules
    "
  />

  <meta name="robots" content="index, follow" />
  <meta name="googlebot" content="index, follow, max-image-preview:large" />

  <meta name="author" content="OMORE Games" />

  <link
    rel="canonical"
    href="https://omore.games/terms"
  />


  {/* OPEN GRAPH */}

  <meta property="og:type" content="website" />

  <meta
    property="og:title"
    content="Terms of Service | OMORE Games"
  />

  <meta
    property="og:description"
    content="Official rules and guidelines for using OMORE Games and Neuralis."
  />

  <meta
    property="og:url"
    content="https://omore.games/terms"
  />

  <meta
    property="og:site_name"
    content="OMORE Games"
  />

  <meta
    property="og:image"
    content="https://omore.games/og-cover.png"
  />


  {/* TWITTER */}

  <meta name="twitter:card" content="summary_large_image" />

  <meta
    name="twitter:title"
    content="Terms of Service | OMORE Games"
  />

  <meta
    name="twitter:description"
    content="Rules for using Neuralis and OMORE Games platform."
  />

  <meta
    name="twitter:image"
    content="https://omore.games/og-cover.png"
  />


  {/* TERMS SCHEMA */}

  <script type="application/ld+json">
    {JSON.stringify({
      "@context": "https://schema.org",
      "@type": "TermsOfService",
      name: "Terms of Service - OMORE Games",
      url: "https://omore.games/terms",
      publisher: {
        "@type": "Organization",
        name: "OMORE Games",
        logo: "https://omore.games/Logo.PNG"
      },
      datePublished: "2026-03-01",
      dateModified: "2026-03-12"
    })}
  </script>


  {/* ORGANIZATION SCHEMA */}

  <script type="application/ld+json">
    {JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "OMORE Games",
      url: "https://omore.games",
      logo: "https://omore.games/Logo.PNG",
      founder: {
        "@type": "Person",
        name: "Omkar More"
      }
    })}
  </script>

</Helmet>

      {/* HERO SECTION */}
      <header className="relative pt-24 pb-16 md:pt-40 md:pb-24 px-6 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[300px] bg-blue-600/5 blur-[120px] rounded-full" />
        
        <div className="max-w-4xl mx-auto relative z-10 text-center">
          <div className="flex justify-center mb-6">
            <FileText className="text-blue-500 w-8 h-8 opacity-50" />
          </div>
          <h1 className="text-5xl md:text-8xl font-black tracking-tighter uppercase mb-4 leading-none">
            TERMS OF <span className="text-zinc-800">SERVICE</span>
          </h1>
          <p className="text-[10px] md:text-xs tracking-[0.5em] text-zinc-600 uppercase font-bold">
            Effective Date: March 12, 2026
          </p>
        </div>
      </header>

      {/* CONTENT SECTION */}
      <main className="pb-32 px-6">
        <article className="max-w-4xl mx-auto">
          {/* SEO Intro Paragraph */}
          <section className="mb-12 text-center">
            <p className="text-zinc-400 text-sm md:text-base leading-relaxed max-w-2xl mx-auto italic font-medium">
              Welcome to OMORE GAMES. By accessing our website and playing our games, 
              including Neuralis, you agree to follow the terms and conditions listed below. 
              These rules help us keep the gaming experience fair and safe for everyone.
            </p>
          </section>

          <div className="grid gap-6">
            {sections.map((sec, i) => (
              <section key={i} className="p-8 border border-zinc-800 bg-zinc-900/5 hover:border-blue-500/30 transition-all rounded-sm group">
                <div className="flex items-center gap-4 mb-4">
                  <div className="group-hover:scale-110 transition-transform">
                    {sec.icon}
                  </div>
                  <h2 className="text-lg font-black uppercase tracking-widest">{sec.title}</h2>
                </div>
                <p className="text-zinc-200 text-sm leading-relaxed font-medium">
                  {sec.desc}
                </p>
              </section>
            ))}
          </div>

          {/* Contact Reference for SEO */}
          <footer className="mt-16 p-8 border-t border-zinc-900 text-center">
            <p className="text-zinc-600 text-[10px] uppercase tracking-widest leading-loose mb-4">
              By using this website, you agree to these rules. If you do not agree, please do not use our services.
            </p>
            <p className="text-zinc-200 text-[10px] uppercase tracking-[0.2em] font-bold">
              Questions? Contact us at <span className="text-blue-500">hello@omkarmore.com</span>
            </p>
          </footer>
        </article>
      </main>
    </div>
  );
}